'use client';

import { FormattedCustomSelect } from '@/components/ui/custom-select';
import { SuaEmpresaPage } from '@/types/product-pages';
import Image from 'next/image';
import {
  interestDataSchema,
  InterestDataType,
  ORIGEM_CONTATO_OPTIONS,
} from './schema';
import { Form } from '@/components/atoms/form';
import { FormattedInput } from '@/components/organism/form/FormattedInput';
import { formatCnpj, formatPhone } from '@/lib/formatters';
import { FieldErrors, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { submitInterestFormData } from '@/data/interest/submitInterestForm';
import React from 'react';
import { toast } from 'sonner';
import { gtmPush } from '@/lib/gtm';
import { Loader2 } from 'lucide-react';
import SuccessModal from './success-modal';

interface InterestFormProps {
  title: string;
  description?: string;
  page: SuaEmpresaPage;
}

const origemContatoOptions = ORIGEM_CONTATO_OPTIONS.map((option) => ({
  value: option,
  label: option,
}));

export default function InterestForm({
  title,
  description,
  page,
}: InterestFormProps) {
  const [successOpen, setSuccessOpen] = React.useState(false);

  const form = useForm<InterestDataType>({
    resolver: zodResolver(interestDataSchema),
  });

  const { isSubmitting } = form.formState;

  const onSuccessWithGTM: SubmitHandler<InterestDataType> = async (data) => {
    try {
      const response = await submitInterestFormData(data);

      if (response === '') {
        setSuccessOpen(true);

        gtmPush({
          event: 'form_submission_success',
          form_type: 'solicitacao_proposta_' + data.page, //ex: solicitacao_proposta_multi
          form_location: window.location.pathname,
          form_id: 'proposal-request-form',
          form_fields_completed: 5,
          form_completion_percentage: 100,
          has_contact_data: data.phone ? true : false,
          contact_origin: data.origemContato,
        });

        form.reset({ page });
      } else {
        if (process.env.NEXT_PUBLIC_DEBUG_FORMS === 'true') {
          console.error('[InterestForm] Envio falhou. Motivo:', response, {
            page: data.page,
          });
        }
        toast.error(`Erro ao enviar os dados. ${response}`);
      }
    } catch (err: unknown) {
      toast.error('Erro ao enviar os dados. Tente novamente mais tarde.');
      console.error('Error submitting interest data:', err);
    }
  };

  const pushErrorGTM = (errors: FieldErrors<InterestDataType>) => {
    // List all the fields you're validating
    const fields: (keyof InterestDataType)[] = [
      'name',
      'email',
      'phone',
      'cnpj',
      'origemContato',
      'page',
    ];

    // Filter out the fields that have errors, with type casting for indexing errors
    const errorFields = fields.filter((field) => errors[field]);

    // Count the number of errors
    const errorCount = errorFields.length;

    // Calculate the form completion percentage
    const fieldsWithoutErrors = fields.length - errorCount;
    const formCompletionPercentage = Math.round(
      (fieldsWithoutErrors / fields.length) * 100
    );

    // Push data to GTM
    gtmPush({
      event: 'form_submission_error',
      form_type: 'solicitacao_proposta',
      error_fields: errorFields, // List all the fields with errors
      error_count: errorCount, // Total number of errors
      form_completion_percentage: formCompletionPercentage, // Form completion percentage
    });
  };

  React.useEffect(() => {
    form.setValue('page', page);
  }, [page, form]);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSuccessWithGTM, (errors) => {
          pushErrorGTM(errors);
        })}
        className="bg-white lg:p-8 h-fit w-full rounded-lg flex flex-col gap-6"
      >
        <div className="flex flex-col gap-2 text-center lg:text-start">
          <h1 className="text-bluecaixa text-2xl font-extrabold">{title}</h1>
          {description && (
            <p className="text-sm text-[#525252] font-thin">{description}</p>
          )}
        </div>

        <div className="flex h-full flex-col gap-3 lg:gap-2 text-sm">
          <div className="flex-1 flex flex-row gap-3 lg:gap-2">
            <FormattedInput
              control={form.control}
              name="name"
              placeholder="Nome Completo*"
              className="flex-1 flex-grow px-4 min-h-12 rounded-md text-black bg-stone-100"
            />
          </div>
          <div className="flex-1 flex flex-col lg:flex-row gap-3 lg:gap-2">
            <FormattedInput
              control={form.control}
              name="email"
              placeholder="E-mail Corporativo*"
              className="flex-1 px-4 min-h-12 rounded-md text-black bg-stone-100"
              type="email"
              minLength={1}
            />
            <FormattedInput
              control={form.control}
              name="phone"
              placeholder="Whatsapp*"
              formatter={formatPhone}
              className="flex-1 px-4 min-h-12 rounded-md text-black bg-stone-100"
              maxLength={15}
            />
          </div>
          <div className="flex-1 flex flex-col lg:flex-row gap-3 lg:gap-2">
            <FormattedInput
              control={form.control}
              name="cnpj"
              placeholder="CNPJ da Empresa*"
              formatter={formatCnpj}
              className="flex-1 px-4 min-h-12 rounded-md text-black bg-stone-100"
              maxLength={18}
              minLength={1}
            />
            <FormattedCustomSelect
              control={form.control}
              name="origemContato"
              options={origemContatoOptions}
              placeholder="Como nos conheceu?"
              className="flex-1 px-4 min-h-12 rounded-md text-black bg-stone-100"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-4 bg-orangecaixa text-white rounded-2xl text-xs lg:font-semibold hover:text-stone-200 flex items-center justify-center gap-2 min-h-[56px] transition-opacity disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:text-white"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-6 w-6 animate-spin" />
              ENVIANDO...
            </>
          ) : (
            <>
              <Image
                height={24}
                width={24}
                src="/icons/arrow-right-circle-brown.png"
                alt="right arrow icon"
              />
              CONTINUAR
            </>
          )}
        </button>
      </form>

      <SuccessModal open={successOpen} onOpenChange={setSuccessOpen} />
    </Form>
  );
}
